/**
 * NotificationBell — the persistent notification center trigger.
 *
 * Two placements:
 *   variant="sidebar" — desktop, lives in the sidebar's bottom utility
 *     cluster. Opens a popover anchored to the bell (bottom-aligned, growing
 *     out of its own corner via the Radix transform origin) with the recent
 *     notifications, mark-all-read, and a "View all" link to /inbox.
 *   variant="header" — the mobile top bar. Opens a bottom sheet with the
 *     same list; "View all" continues to /inbox. A sheet keeps the user in
 *     context — a full page jump for a glance at notifications is heavy.
 *
 * Scope: the badge counts BELL-ELIGIBLE types only — plain chat messages
 * live in the watermark unread model (sidebar Community badge, conversation
 * pills, tab title) and are excluded at the query layer (see
 * BELL_EXCLUDED_TYPES in api/client.ts). Live updates ride the existing
 * realtime plumbing: useNotificationRealtime already maintains the
 * unreadNotificationCount and notifications query caches app-wide.
 */

import { useCallback, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Bell, CheckCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
} from '@/api/hooks';
import { api, type Notification } from '@/api/client';
import { parseActionUrl } from '@/lib/notifications/action-url';
import { formatNotifTime } from '@/lib/notifications/time';
import { NotificationIcon } from './NotificationIcon';

const RECENT_LIMIT = 10;

export function navigateToNotification(
  navigate: ReturnType<typeof useNavigate>,
  notif: Pick<Notification, 'action_url'>,
) {
  if (!notif.action_url) return;
  const target = parseActionUrl(notif.action_url);
  navigate({
    to: target.to,
    search: target.search as never,
    hash: target.hash,
  });
}

/** Three pulsing placeholder rows — matches the app's skeleton-everywhere
 *  loading pattern (a spinner reads as a different, second loading state). */
function ListSkeleton() {
  return (
    <div className="px-3 py-2 space-y-1" aria-hidden>
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-start gap-2.5 rounded-lg px-2.5 py-2.5 animate-pulse">
          <div className="w-7 h-7 rounded-full bg-muted flex-shrink-0" />
          <div className="flex-1 space-y-1.5 pt-0.5">
            <div className="h-3 rounded bg-muted" style={{ width: `${72 - i * 14}%` }} />
            <div className="h-2.5 w-1/3 rounded bg-muted/70" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center px-6 py-10 text-center">
      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
        <Bell className="w-5 h-5 text-muted-foreground/70" />
      </div>
      <p className="text-sm font-semibold text-foreground/80">You're all caught up</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-[220px] leading-relaxed">
        Mentions, replies and event reminders land here.
      </p>
    </div>
  );
}

/**
 * The list itself — shared between the desktop popover and the mobile sheet
 * so the two surfaces read as one system.
 */
function NotificationPanel({ onNavigate }: { onNavigate: () => void }) {
  const { data: notifPages, isLoading } = useNotifications('all');
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const navigate = useNavigate();

  const notifications = (notifPages?.pages[0] ?? []).slice(0, RECENT_LIMIT);

  const handleClick = useCallback((notif: Notification) => {
    if (!notif.is_read) markRead.mutate(notif.id);
    // The in-app click. Recorded whether or not the row navigates: tapping a notification
    // with no action_url is still the builder engaging with it, and `clicked_at` is what the
    // admin funnel counts (migration 629). Fire and forget — `recordNotificationClick`
    // swallows its own failure so the navigation below is never held up.
    void api.recordNotificationClick(notif.id);
    navigateToNotification(navigate, notif);
    onNavigate();
  }, [markRead, navigate, onNavigate]);

  return (
    <div className="flex flex-col min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between pl-4 pr-3 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">Notifications</span>
          {unreadCount > 0 && (
            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--brand-orange))] text-white text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
        <button
          onClick={() => markAllRead.mutate()}
          disabled={markAllRead.isPending || unreadCount === 0}
          className="text-xs font-medium text-foreground/60 hover:text-foreground hover:bg-muted/70 disabled:opacity-40 disabled:hover:bg-transparent rounded-md px-2 py-1 -my-1 flex items-center gap-1 transition-[color,background-color,scale] active:scale-[0.96]"
        >
          <CheckCheck className="w-3.5 h-3.5" />
          Mark all read
        </button>
      </div>

      {/* Recent list — inset rows with their own hover surface (gutter keeps
          the radii concentric with the container) */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {isLoading ? (
          <ListSkeleton />
        ) : notifications.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="px-1.5 py-1.5 space-y-px">
            {notifications.map((notif) => (
              <button
                key={notif.id}
                onClick={() => handleClick(notif)}
                className={`w-full text-left flex items-start gap-2.5 rounded-lg px-2.5 py-2.5 transition-colors hover:bg-muted/60 ${
                  !notif.is_read ? 'bg-muted/30' : ''
                }`}
              >
                <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                  <NotificationIcon type={notif.type} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-[13px] leading-snug line-clamp-2 ${!notif.is_read ? 'font-semibold text-foreground' : 'text-foreground/75'}`}>
                    {notif.title}
                  </p>
                  {notif.message && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{notif.message}</p>
                  )}
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                    {formatNotifTime(notif.created_at)}
                  </p>
                </div>
                {!notif.is_read && (
                  <span
                    className="w-2 h-2 rounded-full bg-[hsl(var(--brand-orange))] flex-shrink-0 self-center"
                    aria-hidden
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <button
        onClick={() => {
          navigate({ to: '/inbox' });
          onNavigate();
        }}
        className="mx-1.5 mb-1.5 mt-0.5 rounded-lg border-t-0 px-4 py-2.5 text-xs font-medium text-foreground/70 hover:text-foreground hover:bg-muted/60 transition-[color,background-color,scale] active:scale-[0.98] text-center"
      >
        View all notifications
      </button>
    </div>
  );
}

function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className="min-w-[20px] h-5 px-1.5 rounded-full bg-[hsl(var(--brand-orange))] text-white text-[11px] font-bold flex items-center justify-center"
      title={`${count} unread notification${count === 1 ? '' : 's'}`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function NotificationBell({
  variant,
  expanded = true,
}: {
  variant: 'sidebar' | 'header';
  expanded?: boolean;
}) {
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const [open, setOpen] = useState(false);

  const bellAriaLabel = unreadCount > 0
    ? `Notifications, ${unreadCount} unread`
    : 'Notifications';

  if (variant === 'header') {
    // Mobile: a bottom sheet keeps the user where they are; the full /inbox
    // page stays one tap away via "View all".
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          <button
            aria-label={bellAriaLabel}
            className="relative p-2 rounded-lg text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-foreground/10 transition-[color,background-color,scale] active:scale-[0.96]"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-orange))] border-2 border-card" aria-hidden />
            )}
          </button>
        </DrawerTrigger>
        <DrawerContent className="max-h-[80dvh]">
          {/* vaul requires a title for a11y; the panel renders its own visible one */}
          <DrawerTitle className="sr-only">Notifications</DrawerTitle>
          <div className="min-h-0 flex flex-col pb-[max(env(safe-area-inset-bottom),0.5rem)]">
            <NotificationPanel onNavigate={() => setOpen(false)} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label={bellAriaLabel}
          title="Notifications"
          className={[
            'flex items-center rounded-lg transition-[color,background-color,scale] duration-150 min-h-[40px] text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-foreground/10 outline-none w-full active:scale-[0.98]',
            expanded ? 'gap-2.5 px-2.5 py-2' : 'justify-center px-0 py-2.5',
          ].join(' ')}
        >
          <div className="relative flex-shrink-0">
            <Bell className="w-5 h-5" />
            {!expanded && unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-orange))] border-2 border-card" aria-hidden />
            )}
          </div>
          {expanded && (
            <>
              <span className="text-[13px] whitespace-nowrap flex-1 text-left font-normal">
                Notifications
              </span>
              <UnreadBadge count={unreadCount} />
            </>
          )}
        </button>
      </PopoverTrigger>
      {/* Border stays the bare `border` token: dark-theme --border carries
          its OWN alpha (white/8%), so an opacity modifier like
          border-border/80 stacks a second alpha into hsl() — invalid CSS,
          which renders the border SOLID WHITE. Elevation comes from the
          layered shadow; the hairline only defines the edge. */}
      {/* Floats clear of the rail (a small sideOffset made it read as a
          docked second sidebar). sideOffset is measured from the TRIGGER,
          whose edge sits ~12px inside the sidebar — 26 yields a ~14px
          visual gap from the rail itself. Bottom lifted slightly off the
          trigger; tighter height cap so it reads as a bubble anchored to
          the bell rather than a full-height panel. */}
      <PopoverContent
        side="right"
        align="end"
        sideOffset={26}
        alignOffset={-8}
        collisionPadding={16}
        className="w-[340px] p-0 rounded-xl shadow-[0_12px_32px_-8px_rgba(0,0,0,0.28),0_4px_12px_-4px_rgba(0,0,0,0.16)] [transform-origin:var(--radix-popover-content-transform-origin)] flex flex-col max-h-[min(64vh,440px)] overflow-hidden"
      >
        <NotificationPanel onNavigate={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
